import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { AmplitudeProductEvent, durationProps } from '@cfa/pathway-fe-shared';

import type { Track } from '@/amplitude/useAmplitude';
import { STORAGE_KEYS } from '@/atoms/storageKeys';
import { store } from '@/atoms/store';
import type {
  PlanBuildTimerEntry,
  PlanBuildTimerSource,
} from '@/routes/TrainingPlans/planBuildTimer.storage';
import {
  DEFAULT_PLAN_BUILD_TIMER_MAP,
  planBuildTimerStorage,
} from '@/routes/TrainingPlans/planBuildTimer.storage';

export type {
  PlanBuildTimerEntry,
  PlanBuildTimerMap,
  PlanBuildTimerSource,
} from '@/routes/TrainingPlans/planBuildTimer.storage';

export const planBuildTimerAtom = atomWithStorage(
  STORAGE_KEYS.PLAN_BUILD_TIMER,
  DEFAULT_PLAN_BUILD_TIMER_MAP,
  planBuildTimerStorage,
  { getOnInit: true },
);

export const markPlanBuildStartedAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      planId: string;
      startedAt: string;
      source: PlanBuildTimerSource;
    },
  ) => {
    const current = get(planBuildTimerAtom);
    set(planBuildTimerAtom, {
      ...current,
      [payload.planId]: {
        startedAt: payload.startedAt,
        source: payload.source,
      },
    });
  },
);

export const hasActiveAssignees = (
  assignments: readonly { status?: string }[] | undefined,
): boolean =>
  (assignments ?? []).some(assignment => {
    const status = assignment.status;
    return (
      typeof status === 'string' && status.length > 0 && status !== 'UNASSIGNED'
    );
  });

export const consumePlanBuildTimer = (
  planId: string,
): PlanBuildTimerEntry | null => {
  const current = store.get(planBuildTimerAtom);
  const entry = current[planId];
  if (!entry) {
    return null;
  }

  const { [planId]: _removed, ...rest } = current;
  store.set(planBuildTimerAtom, rest);
  return entry;
};

export const trackPlanCreateToFirstAssignDuration = (
  track: Track | null,
  planId: string,
  entry: PlanBuildTimerEntry,
): boolean => {
  if (!track) {
    return false;
  }

  const startedAtMs = Date.parse(entry.startedAt);
  const elapsedMs = Number.isFinite(startedAtMs)
    ? Math.max(0, Date.now() - startedAtMs)
    : 0;

  return track(AmplitudeProductEvent.PLAN_CREATE_TO_FIRST_ASSIGN_DURATION, {
    ...durationProps(elapsedMs),
    plan_id: planId,
    creation_source: entry.source,
  });
};

export const trackFirstAssignDurationIfMarked = (
  track: Track | null,
  planId: string,
) => {
  // Keep the marker when Amplitude is unavailable so a later assign can still emit.
  if (!track) {
    return;
  }

  const entry = store.get(planBuildTimerAtom)[planId];
  if (!entry) {
    return;
  }

  const sent = trackPlanCreateToFirstAssignDuration(track, planId, entry);
  if (sent) {
    consumePlanBuildTimer(planId);
  }
};
